import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import type { ClientRecord, RulesConfig } from '../domain/types';
import { rulesConfigSchema } from '../domain/schemas';
import { parseCsv } from '../domain/csv';
import { getAllClients, getMeta, putClients, setMeta } from '../persistence/db';
import { recordAudit } from '../persistence/audit';
import { classify } from '../domain/rules';

export type View =
  | { kind: 'list' }
  | { kind: 'intake' }
  | { kind: 'detail'; clientId: string };

interface State {
  view: View;
  clients: ClientRecord[];
  rules: RulesConfig | null;
  currentRm: string | null;
  loading: boolean;
  error: string | null;
  csvIngestErrors: Array<{ rowIndex: number; clientIdHint: string; message: string }>;
}

type Action =
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_RULES'; rules: RulesConfig }
  | { type: 'SET_CLIENTS'; clients: ClientRecord[] }
  | { type: 'ADD_CLIENT'; client: ClientRecord }
  | { type: 'SET_VIEW'; view: View }
  | { type: 'SET_RM'; rm: string }
  | {
      type: 'SET_CSV_ERRORS';
      errors: Array<{ rowIndex: number; clientIdHint: string; message: string }>;
    };

const initialState: State = {
  view: { kind: 'list' },
  clients: [],
  rules: null,
  currentRm: null,
  loading: true,
  error: null,
  csvIngestErrors: [],
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.loading };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'SET_RULES':
      return { ...state, rules: action.rules };
    case 'SET_CLIENTS':
      return { ...state, clients: action.clients };
    case 'ADD_CLIENT':
      return { ...state, clients: [action.client, ...state.clients] };
    case 'SET_VIEW':
      return { ...state, view: action.view };
    case 'SET_RM':
      return { ...state, currentRm: action.rm };
    case 'SET_CSV_ERRORS':
      return { ...state, csvIngestErrors: action.errors };
    default:
      return state;
  }
}

interface ContextValue {
  state: State;
  setView: (view: View) => void;
  setRm: (rm: string) => void;
  addClient: (record: ClientRecord) => Promise<void>;
}

const AppStateContext = createContext<ContextValue | null>(null);

const CSV_INGEST_FLAG = 'csv-ingested-v1';

export function AppStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    void (async () => {
      try {
        const rulesRes = await fetch('/rules.json', { cache: 'no-store' });
        if (!rulesRes.ok) throw new Error(`Failed to load rules.json: ${rulesRes.status}`);
        const rulesRaw: unknown = await rulesRes.json();
        const rules = rulesConfigSchema.parse(rulesRaw);
        dispatch({ type: 'SET_RULES', rules });

        const alreadyIngested = await getMeta<boolean>(CSV_INGEST_FLAG);
        if (!alreadyIngested) {
          const csvRes = await fetch('/client_onboarding.csv', { cache: 'no-store' });
          if (!csvRes.ok) throw new Error(`Failed to load CSV: ${csvRes.status}`);
          const csvText = await csvRes.text();
          const { ok, errors } = parseCsv(csvText);
          if (ok.length > 0) {
            await putClients(ok);
          }
          await setMeta(CSV_INGEST_FLAG, true);
          if (errors.length > 0) {
            dispatch({ type: 'SET_CSV_ERRORS', errors });
          }

          // Audit the bulk ingest — every record gets a contemporaneous entry.
          await Promise.all(
            ok.map((record) =>
              recordAudit({
                action: 'CSV_INGESTED',
                actor: 'system:csv-import',
                record,
                classification: classify(record, rules),
              }),
            ),
          );
        }

        const clients = await getAllClients();
        clients.sort((a, b) => b.onboarding_date.localeCompare(a.onboarding_date));
        dispatch({ type: 'SET_CLIENTS', clients });
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          error: err instanceof Error ? err.message : 'Unknown error during bootstrap',
        });
      } finally {
        dispatch({ type: 'SET_LOADING', loading: false });
      }
    })();
  }, []);

  const setView = useCallback((view: View) => dispatch({ type: 'SET_VIEW', view }), []);
  const setRm = useCallback((rm: string) => dispatch({ type: 'SET_RM', rm }), []);

  const addClient = useCallback(async (record: ClientRecord) => {
    dispatch({ type: 'ADD_CLIENT', client: record });
  }, []);

  const value = useMemo<ContextValue>(
    () => ({ state, setView, setRm, addClient }),
    [state, setView, setRm, addClient],
  );

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState(): ContextValue {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppState must be used inside AppStateProvider');
  return ctx;
}
